/* eslint-disable @typescript-eslint/member-ordering */
import { Component, effect, input, OnChanges, OnInit, output, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { faComment } from '@fortawesome/pro-regular-svg-icons';
import { ToastController } from '@ionic/angular';

import { Comment, CommentsService, HashtagCount } from '@empathy/comments/data';

@Component({
  selector: 'empathy-comments-list',
  templateUrl: './comments-list.component.html',
  styleUrls: ['./comments-list.component.scss']
})
export class CommentsListComponent implements OnInit, OnChanges {
  allowFilterRemoval = input<boolean>();
  comments = input<Comment[]>([]);
  filterValue = input<string>();
  modifyHashtagCallback = input<(message: string) => string>();
  readonly = input<boolean>();
  triggerCommentReload = input<number>();

  actionComment = output<string>();
  addComment = output<string>();
  computedHashtags = output<HashtagCount[]>();
  postReply = output<Comment>();
  updateComment = output<Comment>();
  selectHashtag = output<string>();

  activeHashtag$ = signal('');
  selectedHashtag$ = signal('');
  commentsFilterValue$ = signal('');
  firstTodayCommentId$ = signal('');
  filteredComments$ = signal<Comment[]>([]);
  nonResolvedCommentsCount$ = signal(0);
  resolvedCommentsCount$ = signal(0);
  sourceComments$ = signal<Comment[]>([]);
  uniqueHashtags$ = signal([]);

  faComment = faComment;

  filterControl = new FormControl(false);

  constructor(
    private commentsService: CommentsService,
    private toastController: ToastController
  ) {
    effect(
      () => {
        if (this.comments() && this.modifyHashtagCallback()) {
          this.comments().forEach((comment) => {
            const hashtagRegex = /#\S+/g;

            comment.commentText = comment.commentText.replace(hashtagRegex, (match) => this.modifyHashtagCallback()(match));
          });

          this.sourceComments$.set(this.comments());
          this.filterControl.setValue(false);
        }
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        if (this.comments()) {
          this.sourceComments$.set(this.comments());
          this.filterControl.setValue(false);
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit(): void {
    this.filterControl.valueChanges.subscribe((value) => {
      this.initialFilterComments(value);
    });
  }

  ngOnChanges(): void {
    if (this.filterValue()) {
      this.activeHashtag$.set(this.filterValue());
      this.onFilterComments(this.filterValue());
    }
  }

  addNewComment(questionId: string): void {
    this.addComment.emit(questionId);

    requestAnimationFrame(() => {
      document.getElementById(`comment-${this.sourceComments$().length}`).scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  onPostCommentReply(reply: Comment): void {
    const rootComment = this.sourceComments$().find((c) => c.id === reply.replyToCommentId);

    if (rootComment) {
      this.postReply.emit(reply);
    }
  }

  onSaveChanges(newComment: Comment): void {
    this.updateComment.emit(newComment);
  }

  onResolveThread(id: string): void {
    this.commentsService.resolveComment(id).subscribe((resolved) => {
      if (!resolved) {
        this.showToast('Cannot resolve comment');
      } else {
        const index = this.sourceComments$().findIndex((c) => c.id === id);

        this.sourceComments$.update((comments) => {
          comments[index].resolvedInd = true;
          return comments;
        });

        this.initialFilterComments(this.filterControl.value);
      }
    });
  }

  onEmitHashtag(hashtag: string): void {
    this.selectHashtag.emit(hashtag);
  }

  onSingleFilterComments(hashtag: string): void {
    this.selectedHashtag$.set(hashtag);
    this.filteredComments$.set(this.filterCommentsByConditionalAndSingleHashtag(this.filterControl.value, hashtag));
    this.actionsAfterModifyingList();
    this.actionComment.emit(hashtag);
  }

  onFilterComments(hashtag: string): void {
    const hashtagNormalize = hashtag.toLocaleLowerCase();

    if (this.activeHashtag$() === hashtagNormalize) {
      this.activeHashtag$.set('');
    } else {
      this.activeHashtag$.set(hashtagNormalize);
    }

    this.selectedHashtag$.set(this.activeHashtag$());

    this.filteredComments$.set(this.filterCommentsByConditional(this.filterControl.value));
    this.actionsAfterModifyingList();
    this.actionComment.emit(this.activeHashtag$());
  }

  private actionsAfterModifyingList(): void {
    this.firstTodayCommentId$.set('');
    this.setTodayFirstComment();
    this.parseUniqueHashtags(this.extractHashtags(this.sourceComments$()));
  }

  private parseUniqueHashtags(uniqueHashtags: string[]): void {
    const orderedCounts: HashtagCount[] = this.countHashtags(uniqueHashtags);

    this.uniqueHashtags$.set(orderedCounts);
  }

  private parsePendingHashtags(uniqueHashtags: string[]): HashtagCount[] {
    const orderedCounts: HashtagCount[] = this.countHashtags(uniqueHashtags);

    return orderedCounts;
  }

  private countHashtags(uniqueHashtags: string[]): HashtagCount[] {
    const counts: Map<string, number> = new Map();
    let orderedCounts: HashtagCount[] = [];

    for (const hashtag of uniqueHashtags) {
      counts.set(hashtag, (counts.get(hashtag) || 0) + 1);
    }

    for (const [hashtag, count] of counts) {
      orderedCounts.push({ count: -count, hashtag });
    }

    orderedCounts.sort((a, b) => a.count - b.count);
    orderedCounts = orderedCounts.map((el) => ({ count: Math.abs(el.count), hashtag: el.hashtag }));
    return orderedCounts;
  }

  private extractHashtags(comments: Comment[]): string[] {
    return comments.reduce((acc, comment) => {
      const commentHashtags = comment.commentText.toLocaleLowerCase().match(/#\w+/g) || [];
      const uniqueHashtags = new Set(commentHashtags);
      return [...acc, ...uniqueHashtags];
    }, [] as string[]);
  }

  private extractPendingHashtags(comments: Comment[]): string[] {
    return comments
      .filter((c) => !c.resolvedInd)
      .reduce((acc, comment) => {
        const commentHashtags = comment.commentText.toLocaleLowerCase().match(/#\w+/g) || [];
        const uniqueHashtags = new Set(commentHashtags);
        return [...acc, ...uniqueHashtags];
      }, [] as string[]);
  }

  private initialFilterComments(resolved: boolean): void {
    this.filteredComments$.set(this.filterCommentsByConditional(resolved));
    this.actionsAfterModifyingList();
    this.computedHashtags.emit(this.parsePendingHashtags(this.extractPendingHashtags(this.sourceComments$())));
  }

  private filterCommentsByHashtag(comments: Comment[], hashtag: string): Comment[] {
    return comments.filter((comment) => {
      const hashtags = this.extractHashtags([comment]);
      return hashtags.includes(hashtag) || !comment.commentText;
    });
  }

  private filterCommentsByResolved(comments: Comment[], resolved: boolean): Comment[] {
    return comments.filter((comment) => !!comment.resolvedInd === resolved);
  }

  private filterCommentsByConditionalAndSingleHashtag(resolved: boolean, hashtag: string): Comment[] {
    let firstFilterComments = this.sourceComments$();

    if (hashtag) {
      firstFilterComments = this.filterCommentsByHashtag(firstFilterComments, hashtag);
    }

    const filteredCommentsByResolved = this.filterCommentsByResolved(firstFilterComments, resolved);

    const nonResolvedCount = this.filterCommentsByResolved(firstFilterComments, false).length;
    this.nonResolvedCommentsCount$.set(nonResolvedCount > 0 ? nonResolvedCount - 1 : 0);
    this.resolvedCommentsCount$.set(this.filterCommentsByResolved(firstFilterComments, true).length);

    return filteredCommentsByResolved;
  }

  private filterCommentsByConditional(resolved: boolean): Comment[] {
    return this.filterCommentsByConditionalAndSingleHashtag(resolved, this.selectedHashtag$());
  }

  private setTodayFirstComment(): void {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 999);

    this.filteredComments$().forEach((comment) => {
      const commentDate = new Date(comment.commentDateTime);

      if (commentDate >= todayStart && commentDate <= todayEnd && !this.firstTodayCommentId$()) {
        this.firstTodayCommentId$.set(comment.id);
      }
    });
  }

  private async showToast(toastMessage: string): Promise<void> {
    const toast = await this.toastController.create({
      message: toastMessage,
      duration: 1500,
      position: 'top'
    });

    await toast.present();
  }
}
